import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Table from "../../components/Table.tsx";
import styled from "styled-components";
import Pagination from "../../components/Pagination.tsx";
import Filters from "../../components/Filter.tsx";
import { Conversation } from "../../types/Conversation.ts";
import { fetchConversations, fetchConversationFilters, ConversationFilters as ApiFilters, type ConversationFilterOptions } from "../../services/conversations.service.ts";
import { WidePageContainer, PageTitle } from "../../components/Layout.tsx";
import { parseApiError } from "../../utils/errorHandler.ts";
import type { FilterConfig, ActiveFilters } from "../../types/Filters.ts";


const ErrorMessage = styled.div`
  background-color: #ffebee;
  color: #c62828;
  padding: 1rem;
  border-radius: 8px;
  margin: 1rem 0;
  border-left: 4px solid #c62828;
  transition: background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease;

  .dark-mode & {
    background-color: #3a1f1f;
    color: #ff6b6b;
    border-left-color: #ff6b6b;
  }
`;

const LoadingMessage = styled.div`
  color: #666;
  padding: 1rem;
  text-align: center;
  transition: color 0.3s ease;

  .dark-mode & {
    color: #b0b0b0;
  }
`;

const FilterToggleButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: #f5f5f5;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 0.95rem;
  cursor: pointer;
  color: #001f52;
  transition: all 0.2s ease;
  align-self: flex-end;

  &:hover {
    background: #e0e0e0;
    border-color: #64da4c;
  }

  .dark-mode & {
    background: #1f1f2e;
    border-color: #3a3a4e;
    color: #e0e0e0;

    &:hover {
      background: #3a3a4e;
      border-color: #64da4c;
    }
  }
`;

const ToggleArrow = styled.span<{ $expanded: boolean }>`
  display: inline-block;
  position: relative;
  width: 12px;
  height: 12px;
  margin-left: 0.5rem;
  
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) ${props => props.$expanded ? 'rotate(180deg)' : 'rotate(0deg)'};
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 6px solid #001f52;
    transition: transform 0.2s ease;
  }

  .dark-mode &::after {
    border-top-color: #e0e0e0;
  }
`;


interface ConversationFilters {
  depth0?: string[];
  depth1?: string[];
  depth2?: string[];
  depth3?: string[];
  depth4?: string[];
  rating: string[];
  adminRating: string[];
  hasAdminComment: string;
  hasUserComment: string;
  evaluation: string;
  useForTraining: string;
  pageSize: string;
}

const FILTERS_STORAGE_KEY = "conversationFilters";

export default function Conversations() {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [filterOptions, setFilterOptions] = useState<ConversationFilterOptions>({
    depth0: [],
    depth1: [],
    depth2: [],
    depth3: [],
    depth4: []
  });
  const [page, setPage] = useState<number>(1);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  
  const [filters, setFilters] = useState<ActiveFilters<ConversationFilters>>(() => {
    const saved = localStorage.getItem(FILTERS_STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (!parsed.pageSize) {
          parsed.pageSize = "10";
        }
        if (typeof parsed.rating === "string") {
          parsed.rating = parsed.rating ? [parsed.rating] : [];
        }
        if (!parsed.hasUserComment) {
          parsed.hasUserComment = "";
        }
        if (!parsed.evaluation) {
          parsed.evaluation = "";
        }
        if (!parsed.adminRating) {
          parsed.adminRating = [];
        }
        if (!parsed.useForTraining) {
          parsed.useForTraining = "";
        }
        if (!parsed.depth0) {
          parsed.depth0 = [];
        }
        if (!parsed.depth1) {
          parsed.depth1 = [];
        }
        if (!parsed.depth2) {
          parsed.depth2 = [];
        }
        if (!parsed.depth3) {
          parsed.depth3 = [];
        }
        if (!parsed.depth4) {
          parsed.depth4 = [];
        }
        if (typeof parsed.depth0 === "string") {
          parsed.depth0 = parsed.depth0 ? [parsed.depth0] : [];
        }
        if (typeof parsed.depth1 === "string") {
          parsed.depth1 = parsed.depth1 ? [parsed.depth1] : [];
        }
        if (typeof parsed.depth2 === "string") {
          parsed.depth2 = parsed.depth2 ? [parsed.depth2] : [];
        }
        if (typeof parsed.depth3 === "string") {
          parsed.depth3 = parsed.depth3 ? [parsed.depth3] : [];
        }
        if (typeof parsed.depth4 === "string") {
          parsed.depth4 = parsed.depth4 ? [parsed.depth4] : [];
        }
        return parsed;
      } catch {
        return {
          depth0: [],
          depth1: [],
          depth2: [],
          depth3: [],
          depth4: [],
          rating: [],
          adminRating: [],
          hasAdminComment: "",
          hasUserComment: "",
          evaluation: "",
          useForTraining: "",
          pageSize: "10"
        };
      }
    }
    return {
      depth0: [],
      depth1: [],
      depth2: [],
      depth3: [],
      depth4: [],
      rating: [],
      adminRating: [],
      hasAdminComment: "",
      hasUserComment: "",
      evaluation: "",
      useForTraining: "",
      pageSize: "10"
    };
  });
  
  const navigate = useNavigate();
  
  useEffect(() => {
    localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters));
  }, [filters]);


  useEffect(() => {
    fetchConversationFilters()
      .then((options) => {
        setFilterOptions(options);
      })
      .catch((err) => {
        console.error("Failed to fetch conversation filter options:", err);
      });
  }, []);

  const pageSize = useMemo(() => {
    const size = filters.pageSize ? parseInt(filters.pageSize as string) : 10;
    return size > 0 ? size : 10;
  }, [filters.pageSize]);

  useEffect(() => {
    setPage(1);
  }, [
    filters.depth0,
    filters.depth1,
    filters.depth2,
    filters.depth3,
    filters.depth4,
    filters.rating,
    filters.adminRating,
    filters.hasAdminComment,
    filters.hasUserComment,
    filters.evaluation,
    filters.useForTraining
  ]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    setPage(1);
    
    const apiFilters: ApiFilters = {};
    
    if (filters.rating && Array.isArray(filters.rating) && filters.rating.length > 0) {
      apiFilters.rating = filters.rating.map(r => parseInt(r));
    }
    
    if (filters.adminRating && Array.isArray(filters.adminRating) && filters.adminRating.length > 0) {
      apiFilters.adminRating = filters.adminRating.map(r => parseInt(r));
    }
    
    if (filters.hasAdminComment && filters.hasAdminComment !== "") {
      apiFilters.hasAdminComment = filters.hasAdminComment as "yes" | "no";
    }
    
    if (filters.hasUserComment && filters.hasUserComment !== "") {
      apiFilters.hasUserComment = filters.hasUserComment as "yes" | "no";
    }
    
    if (filters.evaluation && filters.evaluation !== "") {
      apiFilters.evaluation = filters.evaluation as "yes" | "no";
    }
    
    if (filters.useForTraining && filters.useForTraining !== "") {
      apiFilters.useForTraining = filters.useForTraining as "yes" | "no";
    }

    if (filters.depth0 && Array.isArray(filters.depth0) && filters.depth0.length > 0) {
      apiFilters.depth0 = filters.depth0 as string[];
    }
    if (filters.depth1 && Array.isArray(filters.depth1) && filters.depth1.length > 0) {
      apiFilters.depth1 = filters.depth1 as string[];
    }
    if (filters.depth2 && Array.isArray(filters.depth2) && filters.depth2.length > 0) {
      apiFilters.depth2 = filters.depth2 as string[];
    }
    if (filters.depth3 && Array.isArray(filters.depth3) && filters.depth3.length > 0) {
      apiFilters.depth3 = filters.depth3 as string[];
    }
    if (filters.depth4 && Array.isArray(filters.depth4) && filters.depth4.length > 0) {
      apiFilters.depth4 = filters.depth4 as string[];
    }
    
    fetchConversations(Object.keys(apiFilters).length > 0 ? apiFilters : undefined)
      .then((conversationsRes) => {
        const mapped = conversationsRes.data.map(conv => ({
          ...conv,
          evaluated:
            conv.latestAdminUseForTraining == null
              ? "Nej"
              : "Ja",
        }));
        setConversations(mapped);
        setError(null);
      })
      .catch((err) => {
        const userFriendlyError = parseApiError(err);
        setError(userFriendlyError);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    filters.depth0,
    filters.depth1,
    filters.depth2,
    filters.depth3,
    filters.depth4,
    filters.rating,
    filters.adminRating,
    filters.hasAdminComment,
    filters.hasUserComment,
    filters.evaluation,
    filters.useForTraining
  ]);

  useEffect(() => {
    setPage(1);
  }, [pageSize]);

  const paginatedConversations = useMemo(() => {
    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    return conversations.slice(start, end);
  }, [conversations, page, pageSize]);


  const clearFilters = () => {
    const defaultFilters: ConversationFilters = {
      depth0: [],
      depth1: [],
      depth2: [],
      depth3: [],
      depth4: [],
      rating: [],
      adminRating: [],
      hasAdminComment: "",
      hasUserComment: "",
      evaluation: "",
      useForTraining: "",
      pageSize: filters.pageSize || "10",
    };
    setFilters(defaultFilters);
    localStorage.setItem("conversationFilters", JSON.stringify(defaultFilters));
  };

  const essentialFilterConfigs: FilterConfig<ConversationFilters>[] = useMemo(() => [
    {
      key: "depth0",
      label: "Kategori",
      type: "multiselect",
      options: filterOptions.depth0.map(cat => ({ label: cat, value: cat })),
    },
    {
      key: "rating",
      label: "Bruger vurdering",
      type: "multiselect",
      options: [
        { label: "1 stjerne", value: "1" },
        { label: "2 stjerner", value: "2" },
        { label: "3 stjerner", value: "3" },
        { label: "4 stjerner", value: "4" },
        { label: "5 stjerner", value: "5" },
      ],
    },
    {
      key: "useForTraining",
      label: "Bruges til gentræning",
      type: "select",
      options: [
        { label: "Ja", value: "yes" },
        { label: "Nej", value: "no" },
      ],
    },
    {
      key: "pageSize",
      label: "Rækker per side",
      type: "select",
      options: [
        { label: "10", value: "10" },
        { label: "25", value: "25" },
        { label: "50", value: "50" },
      ],
      defaultValue: "10",
    },
  ], [filterOptions]);

  const advancedFilterConfigs: FilterConfig<ConversationFilters>[] = useMemo(() => [
    {
      key: "depth1",
      label: "Producent",
      type: "multiselect",
      options: filterOptions.depth1.map(p => ({ label: p, value: p })),
    },
    {
      key: "depth2",
      label: "Systemtype",
      type: "multiselect",
      options: filterOptions.depth2.map(st => ({ label: st, value: st })),
    },
    {
      key: "depth3",
      label: "Model",
      type: "multiselect",
      options: filterOptions.depth3.map(m => ({ label: m, value: m })),
    },
    {
      key: "depth4",
      label: "Controller",
      type: "multiselect",
      options: filterOptions.depth4.map(c => ({ label: c, value: c })),
    },

    {
      key: "hasUserComment",
      label: "Bruger kommentar",
      type: "select",
      options: [
        { label: "Har kommentar", value: "yes" },
        { label: "Ingen kommentar", value: "no" },
      ],
    },
    {
      key: "adminRating",
      label: "Admin vurdering",
      type: "multiselect",
      options: [
        { label: "1 stjerne", value: "1" },
        { label: "2 stjerner", value: "2" },
        { label: "3 stjerner", value: "3" },
        { label: "4 stjerner", value: "4" },
        { label: "5 stjerner", value: "5" },
      ],
    },
    {
      key: "hasAdminComment",
      label: "Admin kommentar",
      type: "select",
      options: [
        { label: "Har kommentar", value: "yes" },
        { label: "Ingen kommentar", value: "no" },
      ],
    },
    {
      key: "evaluation",
      label: "Evalueret",
      type: "select",
      options: [
        { label: "Evalueret", value: "yes" },
        { label: "Ikke evalueret", value: "no" },
      ],
    },
  ], [filterOptions]);

  const filterConfigs: FilterConfig<ConversationFilters>[] = useMemo(() => {
    const categoryConfig = essentialFilterConfigs.find(c => c.key === 'depth0');
    const userRatingConfig = essentialFilterConfigs.find(c => c.key === 'rating');
    const useForTrainingConfig = essentialFilterConfigs.find(c => c.key === 'useForTraining');
    const rowsPerPageConfig = essentialFilterConfigs.find(c => c.key === 'pageSize');

    return [
      categoryConfig,
      ...advancedFilterConfigs.slice(0, 4),
      userRatingConfig,
      advancedFilterConfigs.find(c => c.key === 'hasUserComment'),
      advancedFilterConfigs.find(c => c.key === 'adminRating'),
      advancedFilterConfigs.find(c => c.key === 'hasAdminComment'),
      advancedFilterConfigs.find(c => c.key === 'evaluation'),
      useForTrainingConfig,
      rowsPerPageConfig,
    ].filter(Boolean) as FilterConfig<ConversationFilters>[];
  }, [essentialFilterConfigs, advancedFilterConfigs]);

  const handleRowClick = (conversation: Conversation) => {
    navigate(`/conversations/${conversation.id}`);
  };

  return (
    <WidePageContainer>
      <PageTitle>Feedback fra samtaler</PageTitle>
      
      {error && <ErrorMessage>{error}</ErrorMessage>}
      
      <Filters<ConversationFilters>
        configs={filtersExpanded ? filterConfigs : essentialFilterConfigs}
        value={filters}
        onChange={setFilters}
        onClear={clearFilters}
        showClearButton={true}
        extraButtons={
          <FilterToggleButton onClick={() => setFiltersExpanded(!filtersExpanded)}>
            {filtersExpanded ? 'Skjul filtre' : 'Vis flere filtre'}
            <ToggleArrow $expanded={filtersExpanded} />
          </FilterToggleButton>
        }
      />

      {loading && <LoadingMessage>Indlæser samtaler...</LoadingMessage>}
      
      {!loading && !error && (
        <>
          <Table<Conversation>
            data={paginatedConversations}
            columns={[
              { key: "id", label: "nr.", minWidth: "40px", width: "40px" },
              { 
                key: "createdAt", 
                label: "Oprettet", 
                minWidth: "70px", 
                width: "70px",
                render: (date: string) => {
                  if (!date) return "-";
                  try {
                    const d = new Date(date);
                    const day = String(d.getDate()).padStart(2, '0');
                    const month = String(d.getMonth() + 1).padStart(2, '0');
                    const year = String(d.getFullYear()).slice(-2);
                    return `${day}-${month}-${year}`;
                  } catch {
                    return date;
                  }
                }
              },
              { 
                key: "depth1" as keyof Conversation, 
                label: "Producent", 
                minWidth: "85px",
                width: "85px",
                render: (_: string, conversation: Conversation) => {
                  return conversation.hierarchyPath?.[1] || "-";
                }
              },
              { 
                key: "depth2" as keyof Conversation, 
                label: "Systemtype", 
                minWidth: "95px",
                width: "95px",
                render: (_: string, conversation: Conversation) => {
                  return conversation.hierarchyPath?.[2] || "-";
                }
              },
              { 
                key: "depth3" as keyof Conversation, 
                label: "Model", 
                minWidth: "65px",
                width: "65px",
                render: (_: string, conversation: Conversation) => {
                  return conversation.hierarchyPath?.[3] || "-";
                }
              },
              { 
                key: "depth4" as keyof Conversation, 
                label: "Controller", 
                minWidth: "80px",
                width: "80px",
                render: (_: string, conversation: Conversation) => {
                  return conversation.hierarchyPath?.[4] || "-";
                }
              },
              { 
                key: "userRating", 
                label: "Bruger vurdering", 
                minWidth: "75px",
                width: "75px",
                render: (userRating: number) => (
                  userRating != null ? String(userRating) : "-"
                )
              },
              {
                key: "userComment",
                label: "Bruger kommentar",
                minWidth: "90px",
                width: "90px",
                render: (comment?: string) => {
                  if (!comment) return "-";
                  const maxLength = 25;
                  return comment.length > maxLength
                    ? `${comment.slice(0, maxLength).trim()}...`
                    : comment;
                }
              },
              { 
                key: "latestAdminRating", 
                label: "Admin vurdering", 
                minWidth: "75px",
                width: "75px",
                render: (latestAdminRating: number, conversation: Conversation) => (
                  conversation.latestAdminRating != null ? String(conversation.latestAdminRating) : "-"
                )
              },
              {
                key: "latestAdminComment",
                label: "Admin kommentar",
                minWidth: "95px",
                width: "95px",
                render: (comment?: string) => {
                  if (!comment) return "-";
                  const maxLength = 25;
                  return comment.length > maxLength
                    ? `${comment.slice(0, maxLength).trim()}...`
                    : comment;
                }
              },
              { 
                key: "latestAdminUseForTraining", 
                label: "Gentræning", 
                minWidth: "85px",
                width: "85px",
                render: (latestAdminUseForTraining: boolean | null, conversation: Conversation) => {
                  if (conversation.latestAdminUseForTraining === true) return "Ja";
                  if (conversation.latestAdminUseForTraining === false) return "Nej";
                  return "-";
                }
              },
              { key: "evaluated", label: "Evalueret", minWidth: "75px", width: "75px" },
            ]}
            onRowClick={handleRowClick}
          />

          <Pagination
            page={page}
            pageSize={pageSize}
            totalItems={conversations.length}
            onChange={setPage}
          />
        </>
      )}
    </WidePageContainer>
  );
}
